import React, { useState, useEffect, useRef } from 'react';
import { Layout, ConfigProvider, FloatButton, App as AntdApp, theme } from 'antd';
import Sidebar from './components/Sidebar';
import MapGlobe from './components/MapGlobe';
import { CountryBasicInfo } from './types';
import 'maplibre-gl/dist/maplibre-gl.css';
import './styles/earthdaily-theme.css';
import './App.css';
import { MessageOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { fetchCountryList, fetchCountryData } from './utils/dataLoader';
import HelpModal from './components/HelpModal';


// EarthDaily theme colors for Ant Design
const customTheme = {
  token: {
    colorPrimary: '#15b89a',
    colorLink: '#1d8fc2',
    colorSuccess: '#15b89a',
    colorWarning: '#fcb814',
    colorError: '#ff6b6b',
    borderRadius: 8,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    colorTextPlaceholder: 'rgba(0, 0, 0, 0.5)',
  },
  algorithm: theme.defaultAlgorithm,
};

const { Sider, Content } = Layout;

const AppContent: React.FC = () => {
  const [countries, setCountries] = useState<CountryBasicInfo[]>([]);
  const [activeCountry, setActiveCountry] = useState<string | null>(null);
  const [fullGeoJSON, setFullGeoJSON] = useState<any | null>(null);
  const [legendData, setLegendData] = useState<any | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [activePOI, setActivePOI] = useState<string | null>(null);
  const [activeSeason, setActiveSeason] = useState<string | null>(null);
  const [visibleLevel, setVisibleLevel] = useState<'countries' | 'pois' | 'features'>('countries');
  const [helpModalVisible, setHelpModalVisible] = useState<boolean>(false);
  const [hoveredCropType, setHoveredCropType] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  const mapGlobeRef = useRef<any>(null);


  // Access Ant Design message functionality
  const { message } = AntdApp.useApp();

  // Load country list on startup
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countryList = await fetchCountryList();
        setCountries(countryList);
      }
      catch (error) {
        console.error("Error loading country list:", error);
        message.error({
          content: "Oops! We couldn't load the country list. Let's try again later.",
          duration: 5,
        });
      } finally {
        setLoading(false);
      }
    };

    loadCountries();
  }, []);

  // Handle window resize for mobile layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);



  // Charger les données du pays actif
  useEffect(() => {
    if (activeCountry) {
      fetchCountryData(activeCountry).then(({  fullGeoJSON }) => {
        setFullGeoJSON(fullGeoJSON);
        setLegendData(fullGeoJSON?.legend || null);
      });
    } else {
      setFullGeoJSON(null);
      setLegendData(null);
    }
  }, [activeCountry]);


  const handleActivePOIChange = (poiName: string | null) => {
    setActivePOI(poiName);
  };

  const handleActiveSeasonChange = (season: string | null) => {
    setActiveSeason(season);
  };

  const handleVisibleLevelChange = (level: 'countries' | 'pois' | 'features') => {
    setVisibleLevel(level);
  };

  const handleActiveCountryChange = (countryName: string | null) => {
    // Vérifier si le pays a réellement changé
    if (countryName === activeCountry) {
      return;
    }

    setActiveCountry(countryName);
    setActivePOI(null);

  };

  const showContactModal = (): void => {
    window.open('https://earthdaily.com/contact', '_blank');
    // If help modal is open, close it
    if (helpModalVisible) {
      setHelpModalVisible(false);
    }
  };


  const showHelpModal = (): void => {
    setHelpModalVisible(true);
  };

  const hideHelpModal = (): void => {
    setHelpModalVisible(false);
  };

  const handleCountryClick = (countryName: string) => {
    if (mapGlobeRef.current && mapGlobeRef.current.handleCountryMarkerClick) {
      mapGlobeRef.current.handleCountryMarkerClick(countryName);
    }
  };

  const handlePOIClick = (poiName: string) => {
    if (mapGlobeRef.current && mapGlobeRef.current.handlePOIMarkerClick) {
      mapGlobeRef.current.handlePOIMarkerClick(poiName);
    }
  };

  const handleSeasonClick = (season: number) => {
    if (mapGlobeRef.current && mapGlobeRef.current.handleSeasonChange) {
      mapGlobeRef.current.handleSeasonChange(season);
    }
  };

  const handleCropFilterChange = (cropTypes: string[]) => {
    if (mapGlobeRef.current && mapGlobeRef.current.handleCropFilterChange) {
      mapGlobeRef.current.handleCropFilterChange(cropTypes);
    }
  };

  const handleCropHover = (cropType: string | null) => {
    setHoveredCropType(cropType);
    if (mapGlobeRef.current && mapGlobeRef.current.handleCropHover) {
      mapGlobeRef.current.handleCropHover(cropType);
    }
  };

  const handleNavigate = (level: 'countries' | 'pois' | 'features') => {
    setVisibleLevel(level);
    if (level === 'countries') {
      setActiveCountry(null);
      setActivePOI(null);
      if (mapGlobeRef.current && mapGlobeRef.current.resetToGlobalView) {
        mapGlobeRef.current.resetToGlobalView();
      }
    } else if (level === 'pois') {
      setActivePOI(null);
    }
  };


  return (
    <Layout style={{ minHeight: '100vh', flexDirection: isMobile ? 'column' : 'row' }}>
      {!isMobile ? (
        <>
          <Sider
            width={360}
            theme="light"
            breakpoint="lg"
            collapsedWidth={0}
            style={{ zIndex: 10 }}
          >
            <Sidebar
              countries={countries}
              activeCountry={activeCountry}
              loading={loading}
              geoJSONData={fullGeoJSON}
              legendData={legendData}
              activePOI={activePOI}
              activeSeason={activeSeason}
              visibleLevel={visibleLevel}
              hoveredCropType={hoveredCropType}
              onCountryClick={handleCountryClick}
              onPOIClick={handlePOIClick}
              onSeasonClick={handleSeasonClick}
              onCropFilterChange={handleCropFilterChange}
              onCropHover={handleCropHover}
              onNavigate={handleNavigate}
            />
          </Sider>
          <Content style={{ marginLeft: 300, height: '100vh' }}>
            <MapGlobe
              ref={mapGlobeRef}
              countriesData={countries}
              activeSeason={activeSeason}
              onActivePOIChange={handleActivePOIChange}
              onActiveCountryChange={handleActiveCountryChange}
              onVisibleLevelChange={handleVisibleLevelChange}
              onActiveSeasonChange={handleActiveSeasonChange}
              onCropHover={handleCropHover}
            />
          </Content>
        </>
      ) : (
        <>
          <div style={{
            padding: '10px 15px',
            backgroundColor: '#f9f9f9',
            textAlign: 'center',
            borderBottom: '1px solid rgba(0,0,0,0.05)'
          }}>
            <img
              src="https://ag.earthdaily.com/hubfs/Main%20Website%202025/LOB%20Logos/LoB_logo_Agriculture@4x.png"
              alt="EarthDaily Logo"
              style={{ height: '30px', display: 'block', margin: '0 auto' }}
            />
            <div style={{ marginTop: '4px', fontSize: '12px', color: '#214b64', opacity: 0.8 }}>
              Explore geospatial agricultural data
            </div>
          </div>
          <Content style={{ height: '50vh' }}>
            <MapGlobe
              ref={mapGlobeRef}
              countriesData={countries}
              activeSeason={activeSeason}
              onActivePOIChange={handleActivePOIChange}
              onActiveCountryChange={handleActiveCountryChange}
              onVisibleLevelChange={handleVisibleLevelChange}
              onActiveSeasonChange={handleActiveSeasonChange}
              onCropHover={handleCropHover}
            />
          </Content>
          <div style={{ height: 'calc(50vh - 60px)', overflowY: 'auto' }}>
            <Sidebar
              countries={countries}
              activeCountry={activeCountry}
              loading={loading}
              geoJSONData={fullGeoJSON}
              legendData={legendData}
              activePOI={activePOI}
              activeSeason={activeSeason}
              visibleLevel={visibleLevel}
              hoveredCropType={hoveredCropType}
              onCountryClick={handleCountryClick}
              onPOIClick={handlePOIClick}
              onSeasonClick={handleSeasonClick}
              onCropFilterChange={handleCropFilterChange}
              onCropHover={handleCropHover}
              onNavigate={handleNavigate}
            />
          </div>
        </>
      )}
      <FloatButton.Group shape="circle" style={{ insetInlineEnd: 24 }}>

        <FloatButton
          icon={<QuestionCircleOutlined />}
          type="primary"
          tooltip="More information"
          onClick={showHelpModal}
        />
        <FloatButton
          icon={<MessageOutlined />}
          type="primary"
          tooltip="Contact us"
          onClick={showContactModal}
        />
      </FloatButton.Group>


      {/* Help Modal */}
      <HelpModal
        visible={helpModalVisible}
        onClose={hideHelpModal}
        onContactUs={showContactModal}
      />
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <ConfigProvider theme={customTheme}>
      <AntdApp>
        <AppContent />
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;